import React, { useEffect, useState } from 'react';
import { Table, Tooltip, Button, message } from 'antd';

import { BoxPlotOutlined } from '@ant-design/icons';

const getIQR = (signals) => {
  let q1 = 0, q2 = 0, q3 = 0;
  if (signals.length < 4) return { q1, q2, q3 };
  const sortedSignals = signals.sort((a, b) => a - b);
  if (sortedSignals.length % 2 !== 0) {
    q2 = sortedSignals[Math.floor(sortedSignals.length / 2)];
    const lower = sortedSignals.slice(0, sortedSignals.length / 2);
    const upper = sortedSignals.slice(sortedSignals.length / 2 + 1);
    if (lower.length % 2 !== 0) {
      q1 = lower[Math.floor(lower.length / 2)];
      q3 = upper[Math.floor(upper.length / 2)];
    } else {
      q1 = (lower[(lower.length / 2) - 1] + lower[(lower.length / 2)]) / 2;
      q3 = (upper[(upper.length / 2) - 1] + upper[(upper.length / 2)]) / 2;
    }
  } else {
    q2 = (sortedSignals[(sortedSignals.length / 2) - 1] + sortedSignals[(sortedSignals.length / 2)]) / 2;
    const lower = sortedSignals.slice(0, sortedSignals.length / 2);
    const upper = sortedSignals.slice(sortedSignals.length / 2);
    if (lower.length % 2 !== 0) {
      q1 = lower[Math.floor(lower.length / 2)];
      q3 = upper[Math.floor(upper.length / 2)];
    } else {
      q1 = (lower[(lower.length / 2) - 1] + lower[(lower.length / 2)]) / 2;
      q3 = (upper[(upper.length / 2) - 1] + upper[(upper.length / 2)]) / 2;
    }
  }
  return { q1, q2, q3 };
}


const ResultsTable = ({ record, setTableView }) => {

  const resultTableColumns = [
    {
      title: 'Substance',
      dataIndex: 'substance',
      key: 'substance',
      width: 165,
      align: 'center',
    },
    {
      title: 'Mean',
      dataIndex: 'mean',
      key: 'mean',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.mean - b.mean,
      defaultSortOrder: 'descend',
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Min',
      dataIndex: 'min',
      key: 'min',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.min - b.min,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Q1',
      dataIndex: 'q1',
      key: 'q1',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.q1 - b.q1,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Median',
      dataIndex: 'q2',
      key: 'q2',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.q2 - b.q2,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Q3',
      dataIndex: 'q3',
      key: 'q3',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.q3 - b.q3,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Max',
      dataIndex: 'max',
      key: 'max',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.max - b.max,
      sortDirections: ['descend', 'ascend'],
    },
  ];

  const [resultsTableData, setResultsTableData] = useState([]);

  useEffect(() => {

    if (!(
      record?.results?.length > 0 &&
      record?.results.some(substance => substance.predictions !== null) &&
      !(record?.results?.length === 1 && record?.results[0].substance === 'Unknown')
    )) return;

    const results = record.results.filter(row => {
      // return record.showTrace ? (row.substance !== 'Unknown') : (row.substance !== 'Unknown' && row.is_trace_only === false);
      return row.substance !== 'Unknown';
    })

    if (!results) return;

    let tableData = [];

    for (let i = 0; i < results.length; i++) {
      const { q1, q2, q3 } = getIQR(results[i].predictions);
      tableData.push({
        substance: results[i].substance,
        mean: (results[i].predictions.reduce((a, b) => a + b, 0) / results[i].predictions.length).toFixed(2),
        q1: q1.toFixed(2),
        q2: q2.toFixed(2),
        q3: q3.toFixed(2),
        min: Math.min(...results[i].predictions).toFixed(2),
        max: Math.max(...results[i].predictions).toFixed(2),
      });
    }

    setResultsTableData(tableData);

  }, [record]);

  const saveRowToClipboard = (row) => {
    const rowText = resultTableColumns.map(col => row[col.dataIndex]).join('\t');
    navigator.clipboard.writeText(rowText);
    message.success(
      <>
        <b>{row.substance}</b> values copied to clipboard.
      </>
    );
  };

  return (
    <>
      <div style={{ display: 'grid' }}>
        <Table
          style={{ overflow: 'auto', margin: '16px 0 0 0' }}
          dataSource={resultsTableData}
          columns={resultTableColumns}
          pagination={
            resultsTableData.length > 10
              ? { position: ['bottomCenter'], pageSize: 10 }
              : false
          }
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => saveRowToClipboard(record),
            style: { cursor: 'pointer' }
          })}
        />
        <Tooltip title="Graphic View">
          <Button
            shape='circle' color="default" type="text"
            style={{ margin: '8px 0 0 8px' }}
            icon={<BoxPlotOutlined />}
            onClick={() => { setTableView(false); }}
          />
        </Tooltip>
      </div>
    </>
  )
}

export default ResultsTable;